import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { CAMERA_MODEL, CameraService, TCameraModel } from '@monorepo/services';
import {
  IOperationsList,
  IDefectList,
  TBctxHeaderItem,
  TOperationIssueAndMarkedLocations,
  TDefectiveFlowContainerResult,
  TOperationAnsIssue,
  TLocationCoordinates,
  TDefectiveOperationIssueAndSilhouetteMarkingInfo,
  TOperationAndSelectedGenericMappedIssueList,
  TUpdateFilteredListsV3,
  TOperationAndDeptWiseGenericIssue,
  TDepartmentWiseOperationListAndGenericIssueMap,
  TDefectSilhoutteData,
} from '@monorepo/types';
import { BCTXComputationHelper, EventUtil } from '@monorepo/utils';

@Component({
  selector: 'monorepo-defective-flow-container-v3',
  templateUrl: './defective-flow-container-v3.component.html',
  styleUrls: ['./defective-flow-container-v3.component.scss'],
})
export class DefectiveFlowContainerV3Component implements OnInit, OnChanges {
  @Input() isImageCapturingEnabled = false;
  @Input() bctxHeader: TBctxHeaderItem[] = [];
  @Input() isRecentDefectsSelection = false;
  @Input() recentDefects: TOperationAnsIssue[] = [];
  @Input() departments: string[] = [];

  @Input() defectSilhouetteData!: TDefectSilhoutteData;
  @Input() departmentWiseOperationListAndGenericIssueMap!: TDepartmentWiseOperationListAndGenericIssueMap;

  @Output() cancel = new EventEmitter<'CANCEL'>();
  @Output() done = new EventEmitter<TDefectiveFlowContainerResult>();
  @Output() filterLists = new EventEmitter<TUpdateFilteredListsV3>();

  CAMERA_MODEL = CAMERA_MODEL.identity(EventUtil.getBucketIdFromUniqueEventId(BCTXComputationHelper.getUniqueVId()));

  isOperationSelectionStep: boolean = true;
  isIssueSelectionStep: boolean = false;
  isMarkLocationStep: boolean = false;
  isRecentDefectiveSelectionStep: boolean = false;

  recentDefectiveList: TOperationIssueAndMarkedLocations[] = [];
  selectedOperationList: IOperationsList[] = [];
  selectedRecentDefectiveList: TOperationIssueAndMarkedLocations[] = [];
  operationAndSelectedIssueList: TOperationAndSelectedGenericMappedIssueList[] = [];
  operationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[] = [];
  #bucketInfo: TCameraModel['bucketInfo'] = this.CAMERA_MODEL.bucketInfo;

  constructor(private cameraService: CameraService, private modalCtrl: ModalController) {}

  ngOnChanges(): void {
    this.handleOperationClearAll();
  }

  ngOnInit(): void {
    if (this.isRecentDefectsSelection) {
      this.isOperationSelectionStep = false;
      this.isRecentDefectiveSelectionStep = true;
      this.#createRecentDefectiveList();
    }
  }

  async openCamera() {
    this.cameraService.openModal(this.CAMERA_MODEL).then((v) => {
      console.log('Dismiss Data', v.data);
      if (v.data) this.#bucketInfo = v.data;
    });
  }

  #createRecentDefectiveList() {
    this.recentDefectiveList = this.recentDefects.map((item) => {
      return {
        ...item,
        front_locations: [],
        back_locations: [],
      };
    });
  }

  #getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(
    operation: IOperationsList,
    issue: IDefectList,
    location: TLocationCoordinates,
    isFrontLocation: boolean,
  ) {
    const defectiveOperationIssueAndSilhouetteMarkingInfoItem: TDefectiveOperationIssueAndSilhouetteMarkingInfo = {
      operation: {
        category_id: operation.category_id,
        name: operation.name,
        display: true,
        group_name: '',
        ...(operation.department && {
          department: operation.department,
        }),
      },
      issues: {
        category_id: issue.category_id,
        name: issue.name,
      },
      silhouette: {
        x: location.x,
        y: location.y,
        image: isFrontLocation ? 'front image' : 'back image',
        ...(this.defectSilhouetteData.silhoutteId !== 'DEFAULT' && {
          shillohutte_id: this.defectSilhouetteData.silhoutteId,
        }),
      },
    };
    return defectiveOperationIssueAndSilhouetteMarkingInfoItem;
  }

  handleCancel() {
    this.cancel.emit('CANCEL');
  }

  handleOperationSelectionNext(operationList: IOperationsList[]) {
    const updatedOperationAndSelectedIssueList: TOperationAndSelectedGenericMappedIssueList[] = [];
    let isOperationAndSelectedIssueListChanged = false;
    operationList.forEach((item) => {
      const operationAndSelectedIssueListItem = this.operationAndSelectedIssueList.find(
        (operationAndSelectedIssueListItem) => operationAndSelectedIssueListItem.operation.id === item.category_id,
      );
      if (operationAndSelectedIssueListItem) {
        updatedOperationAndSelectedIssueList.push(operationAndSelectedIssueListItem);
      } else {
        isOperationAndSelectedIssueListChanged = true;
        if (item.department) {
          let newOperationSelected = this.departmentWiseOperationListAndGenericIssueMap[
            item.department
          ].operationsList.find((ele) => ele.id === item.category_id);
          if (newOperationSelected) {
            const mappedOperationValue: TOperationAndSelectedGenericMappedIssueList['operation'] = {
              category_id: newOperationSelected.id,
              group_id: [],
              group_name: newOperationSelected.name,
              id: newOperationSelected.id,
              name: newOperationSelected.name,
              department: item.department,
            };
            updatedOperationAndSelectedIssueList.push({
              operation: mappedOperationValue,
              selectedGenericIssue: [],
              selectedMappedIssue: [],
            });
          }
        }
      }
    });
    if (isOperationAndSelectedIssueListChanged || this.operationAndSelectedIssueList.length !== operationList.length) {
      this.operationAndSelectedIssueList = updatedOperationAndSelectedIssueList.map((item) => {
        return {
          ...item,
        };
      });
    } else this.operationAndSelectedIssueList = updatedOperationAndSelectedIssueList;

    this.isOperationSelectionStep = false;
    this.isIssueSelectionStep = true;
  }

  handleRecentDefectiveSelectionNext(operationIssueAndMarkedLocations: TOperationIssueAndMarkedLocations[]) {
    this.operationIssueAndMarkedLocationsList = operationIssueAndMarkedLocations;
    this.selectedRecentDefectiveList = operationIssueAndMarkedLocations;
    this.isRecentDefectiveSelectionStep = false;
    this.isMarkLocationStep = true;
  }

  handleOperationOrRecentDefectiveSelectionNext(list: TOperationIssueAndMarkedLocations[] | IOperationsList[]) {
    if (this.isRecentDefectsSelection)
      this.handleRecentDefectiveSelectionNext(list as TOperationIssueAndMarkedLocations[]);
    else this.handleOperationSelectionNext(list as IOperationsList[]);
  }

  handleIssueSelectionBack(operationAndSelectedIssueList: TOperationAndSelectedGenericMappedIssueList[]) {
    this.operationAndSelectedIssueList = operationAndSelectedIssueList;
    this.selectedOperationList = this.operationAndSelectedIssueList.map((item) => item.operation);
    this.isIssueSelectionStep = false;
    this.isOperationSelectionStep = true;
  }

  handleIssueSelectionNext(operationAndSelectedIssueList: TOperationAndSelectedGenericMappedIssueList[]) {
    this.operationAndSelectedIssueList = operationAndSelectedIssueList;
    const updatedOperationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[] = [];
    let isOperationIssueAndMarkedLocationsListChanged = false;
    operationAndSelectedIssueList.forEach((item) => {
      item.selectedGenericIssue.forEach((issue) => {
        const operationIssueAndMarkedLocationsListItem = this.operationIssueAndMarkedLocationsList.find(
          (operationIssueAndMarkedLocationsListItem) =>
            operationIssueAndMarkedLocationsListItem.operation.category_id === item.operation.category_id &&
            operationIssueAndMarkedLocationsListItem.issue.category_id === issue.category_id,
        );
        if (operationIssueAndMarkedLocationsListItem)
          updatedOperationIssueAndMarkedLocationsList.push(operationIssueAndMarkedLocationsListItem);
        else {
          isOperationIssueAndMarkedLocationsListChanged = true;
          updatedOperationIssueAndMarkedLocationsList.push({
            operation: item.operation,
            issue: issue,
            back_locations: [],
            front_locations: [],
          });
        }
      });
      item.selectedMappedIssue.forEach((issue) => {
        const operationIssueAndMarkedLocationsListItem = this.operationIssueAndMarkedLocationsList.find(
          (operationIssueAndMarkedLocationsListItem) =>
            operationIssueAndMarkedLocationsListItem.operation.category_id === item.operation.category_id &&
            operationIssueAndMarkedLocationsListItem.issue.category_id === issue.category_id,
        );
        if (operationIssueAndMarkedLocationsListItem)
          updatedOperationIssueAndMarkedLocationsList.push(operationIssueAndMarkedLocationsListItem);
        else {
          isOperationIssueAndMarkedLocationsListChanged = true;
          updatedOperationIssueAndMarkedLocationsList.push({
            operation: item.operation,
            issue: issue,
            back_locations: [],
            front_locations: [],
          });
        }
      });
    });
    if (
      isOperationIssueAndMarkedLocationsListChanged ||
      this.operationIssueAndMarkedLocationsList.length !== updatedOperationIssueAndMarkedLocationsList.length
    ) {
      this.operationIssueAndMarkedLocationsList = updatedOperationIssueAndMarkedLocationsList.map((item) => {
        return {
          ...item,
          front_locations: [],
          back_locations: [],
        };
      });
    } else this.operationIssueAndMarkedLocationsList = updatedOperationIssueAndMarkedLocationsList;
    this.isIssueSelectionStep = false;
    this.isMarkLocationStep = true;
  }

  handleMarkLocationBack(operationIssueAndMarkedLocationsList: TOperationIssueAndMarkedLocations[]) {
    this.operationIssueAndMarkedLocationsList = operationIssueAndMarkedLocationsList;
    this.isMarkLocationStep = false;
    if (this.isRecentDefectsSelection) {
      this.selectedRecentDefectiveList = operationIssueAndMarkedLocationsList;
      this.isRecentDefectiveSelectionStep = true;
    } else this.isIssueSelectionStep = true;
  }

  handleDone(defectiveOperationIssueAndSilhouetteMarkedLocationList: TOperationIssueAndMarkedLocations[]) {
    const defectiveOperationIssueAndSilhouetteMarkingInfoList: TDefectiveOperationIssueAndSilhouetteMarkingInfo[] = [];
    defectiveOperationIssueAndSilhouetteMarkedLocationList.forEach((item) => {
      if (
        !this.isRecentDefectsSelection &&
        Object.keys(this.departmentWiseOperationListAndGenericIssueMap).length <= 1
      ) {
        delete item.operation.department;
      }
      item.back_locations.forEach((location) =>
        defectiveOperationIssueAndSilhouetteMarkingInfoList.push(
          this.#getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(item.operation, item.issue, location, false),
        ),
      );
      item.front_locations.forEach((location) =>
        defectiveOperationIssueAndSilhouetteMarkingInfoList.push(
          this.#getDefectiveOperationIssueAndSilhouetteMarkingInfoItem(item.operation, item.issue, location, true),
        ),
      );
    });
    console.log('Bucket info from defective-flow-container', this.#bucketInfo);
    this.done.emit({
      result: defectiveOperationIssueAndSilhouetteMarkingInfoList,
      defects: defectiveOperationIssueAndSilhouetteMarkedLocationList.map((item) => {
        return {
          operation: item.operation,
          issue: item.issue,
        };
      }),
      bucketInfo: this.#bucketInfo,
    });
  }

  handleOperationClearAll() {
    this.selectedOperationList = [];
    this.selectedRecentDefectiveList = [];
    this.handleIssueClearAll();
    this.handleMarkLocationClearAll();
  }

  onViewAll(updatedFilterList: TUpdateFilteredListsV3) {
    this.filterLists.emit(updatedFilterList);
  }

  handleIssueClearAll() {
    this.operationAndSelectedIssueList = this.operationAndSelectedIssueList.map((item) => {
      return {
        ...item,
        selectedGenericIssue: [],
        selectedMappedIssue: [],
      };
    });
    this.handleMarkLocationClearAll();
  }

  handleMarkLocationClearAll() {
    this.operationIssueAndMarkedLocationsList = this.operationIssueAndMarkedLocationsList.map((item) => {
      return {
        ...item,
        front_locations: [],
        back_locations: [],
      };
    });
  }
}
